<template>
  <div class="booking-form bg-white rounded-lg shadow-lg p-8">
    <!-- Form Steps Indicator -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <div :class="[
            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
            currentStep >= 1 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
          ]">
            1
          </div>
          <div class="hidden sm:block text-sm font-medium text-gray-900">Duur kiezen</div>
        </div>
        
        <div class="flex items-center space-x-4">
          <div :class="[
            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
            currentStep >= 2 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
          ]">
            2
          </div>
          <div class="hidden sm:block text-sm font-medium text-gray-900">Datum & Tijd</div>
        </div>
        
        <div class="flex items-center space-x-4">
          <div :class="[
            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
            currentStep >= 3 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
          ]">
            3
          </div>
          <div class="hidden sm:block text-sm font-medium text-gray-900">Gegevens</div>
        </div>
      </div>
      
      <!-- Progress Bar -->
      <div class="mt-4">
        <div class="bg-gray-200 rounded-full h-2">
          <div 
            class="bg-amber-500 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${(currentStep / 3) * 100}%` }"
          ></div>
        </div>
      </div>
    </div>

    <div
      v-if="recoveryNotice"
      class="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800"
      role="status"
    >
      {{ recoveryNotice }}
    </div>

    <!-- Step 1: Duration Selection -->
    <div v-if="currentStep === 1" class="step-1">
      <h2 class="text-2xl font-semibold mb-6 text-center">Kies de duur van je sessie</h2>
      
      <!-- Loading state -->
      <div v-if="loadingPricing">
        <SkeletonLoader type="pricing-options" />
      </div>
      
      <div v-else class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <button
          v-for="option in durationOptions"
          :key="option.hours"
          @click="selectDuration(option.hours)"
          :class="[
            'p-6 border-2 rounded-lg transition-all duration-200 hover:shadow-md',
            selectedDuration === option.hours
              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
              : 'border-gray-200 hover:border-gray-300'
          ]"
        >
          <div class="text-2xl font-bold text-gray-900 mb-2">{{ option.hours }} uur</div>
          <div class="text-lg font-semibold text-amber-600 mb-2">
            €{{ option.price }}*
          </div>
          <div class="text-sm text-gray-600">{{ option.description }}</div>
        </button>
      </div>

      <!-- Flexible booking option (loyalty perk for tiers with custom_time_slots) -->
      <div v-if="canBookFlexible" class="mb-8">
        <button
          @click="selectFlexible"
          :class="[
            'w-full p-6 border-2 rounded-lg transition-all duration-200 hover:shadow-md text-left',
            flexibleMode
              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
              : 'border-dashed border-amber-300 hover:border-amber-400'
          ]"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-xl font-bold text-gray-900 mb-1">⏰ Flexibel boeken</div>
              <div class="text-sm text-gray-600">
                Kies zelf je start- en einduur (hele uren, tot een volledige dag) tegen {{ flexibleRateFormatted }} per uur*.
              </div>
            </div>
            <TierBadge
              v-if="currentTier"
              :tier-key="currentTier.key"
              :tier-icon="currentTier.icon"
              :show-name="false"
              size="small"
            />
          </div>
        </button>
      </div>

      <div class="text-center">
        <button
          @click="nextStep"
          :disabled="(!selectedDuration && !flexibleMode) || loadingPricing"
          class="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3"
        >
          Volgende: Datum kiezen
        </button>
      </div>
      
      <!-- VAT Disclaimer -->
      <div class="mt-6 text-xs text-gray-500 text-center">
        * Particulieren betalen inclusief 21% BTW. Zakelijke klanten betalen exclusief BTW (+ 21% BTW).
      </div>
    </div>

    <!-- Step 2: Date & Time Selection -->
    <div v-if="currentStep === 2" class="step-2">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-semibold">Kies datum en tijd</h2>
        <div class="text-sm text-gray-600">
          <template v-if="flexibleMode">Flexibele sessie - kies zelf je uren</template>
          <template v-else>Sessie van {{ selectedDuration }} uur - €{{ selectedDurationOption?.price }}*</template>
        </div>
      </div>

      <!-- Price calculation display -->
      <div v-if="selectedSlot" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="font-medium text-green-900">Geselecteerd tijdslot</h3>
            <p class="text-sm text-green-700">
              {{ formatSelectedDate() }}, {{ formatSelectedTime() }}
            </p>
          </div>
          <div class="text-right">
            <div class="text-lg font-semibold text-green-900">
              {{ displayPrice }}
            </div>
            <div class="text-xs text-green-600">
              {{ totalDurationHours }} uur sessie
              <span v-if="upsellHours" data-testid="step2-upsell">(incl. {{ upsellHours }} uur verlenging)</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Timezone Warning -->
      <div v-if="timezoneDiffers" class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div class="flex items-start">
          <div class="text-amber-400 text-xl mr-3">🌍</div>
          <div>
            <h4 class="text-sm font-medium text-amber-900 mb-1">Tijdzone verschil gedetecteerd</h4>
            <p class="text-sm text-amber-700">
              Deze tijden zijn in Belgische tijd ({{ getTimezoneDisplayName('Europe/Brussels') }}). 
              Jouw tijdzone is {{ getTimezoneDisplayName() }}.
            </p>
            <p class="text-xs text-amber-600 mt-2">
              Geselecteerde tijd in jouw tijdzone: {{ convertToUserTimezone() }}
            </p>
          </div>
        </div>
      </div>

      <BookingCalendar
        :duration-hours="flexibleMode ? 1 : selectedDuration!"
        :customer-type="customerForm.customer_type"
        :flexible="flexibleMode"
        :initial-date="recoveredLead?.booking_date ?? undefined"
        :initial-start-time="recoveredLead?.start_time ?? undefined"
        @slot-selected="onSlotSelected"
        @date-changed="onDateChanged"
      />

      <!-- Verleng-upsell: 1 of 2 extra uren, ingeklapt onder de tijdslotkeuze -->
      <UpsellExtensionPicker
        v-if="selectedSlot && showUpsellPicker && upsellQuote"
        class="mt-6"
        collapsible
        compact
        :upsell="upsellQuote"
        :selected-hours="upsellHours"
        hint="Je kunt je keuze nog aanpassen tot je betaalt."
        @select="chooseUpsellHours"
      />

      <div class="flex items-center justify-between mt-8">
        <button
          @click="previousStep"
          class="btn btn-secondary px-6 py-2"
        >
          Vorige
        </button>
        
        <button
          @click="nextStep"
          :disabled="!selectedSlot"
          class="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3"
        >
          Volgende: Gegevens invullen
        </button>
      </div>
      
      <!-- VAT Disclaimer -->
      <div class="mt-6 text-xs text-gray-500 text-center">
        * Particulieren betalen inclusief 21% BTW. Zakelijke klanten betalen exclusief BTW (+ 21% BTW).
      </div>
    </div>

    <!-- Step 3: Booking Details -->
    <div v-if="currentStep === 3" class="step-3">
      <h2 class="text-2xl font-semibold mb-6">Reservering voltooien</h2>
      
      <!-- Booking Summary -->
      <div class="bg-amber-50 rounded-lg p-6 mb-8">
        <h3 class="font-semibold text-amber-900 mb-4">Samenvatting van je reservering</h3>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span>Datum:</span>
            <span class="font-medium">{{ formatSelectedDate() }}</span>
          </div>
          <div class="flex justify-between">
            <span>Tijd:</span>
            <span class="font-medium">{{ formatSelectedTime() }}</span>
          </div>
          <div class="flex justify-between">
            <span>Duur:</span>
            <span class="font-medium">
              {{ totalDurationHours }} uur
              <span v-if="upsellHours" class="text-amber-700" data-testid="step3-upsell">(incl. {{ upsellHours }} uur verlenging)</span>
            </span>
          </div>
          <div v-if="upsellHours && selectedUpsellOption" class="flex justify-between text-amber-800">
            <span>Verlenging (+{{ upsellHours }} uur):</span>
            <span class="font-medium">+ {{ selectedUpsellOption.price_formatted }}</span>
          </div>
          <div class="border-t border-amber-200 pt-2 mt-4">
            <!-- Tier Discount Info -->
            <div v-if="authStore.isAuthenticated && currentTier && currentTier.discount_percentage > 0" class="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-green-800">Jouw tier voordeel:</span>
                <TierBadge
                  :tier-key="currentTier.key"
                  :tier-icon="currentTier.icon"
                  :show-name="false"
                  size="small"
                />
              </div>
              <div class="flex justify-between text-sm text-green-700">
                <span>{{ currentTier.discount_percentage }}% korting</span>
                <span class="font-semibold">Besparing inbegrepen</span>
              </div>
            </div>

            <div v-if="false" class="space-y-1 text-sm text-amber-800">
              <div class="flex justify-between">
                <span>Subtotaal (excl. BTW):</span>
                <span>{{ formatVatAmount(getVatBreakdown().subtotal_cents) }}</span>
              </div>
              <div class="flex justify-between">
                <span>BTW (21%):</span>
                <span>{{ formatVatAmount(getVatBreakdown().vat_cents) }}</span>
              </div>
            </div>
            <div class="flex justify-between text-lg font-semibold text-amber-900">
              <span>Totaal:</span>
              <span>{{ displayPrice }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Verleng-upsell: ook hier nog aan te passen -->
      <UpsellExtensionPicker
        v-if="selectedSlot && showUpsellPicker && upsellQuote"
        class="mb-6"
        :upsell="upsellQuote"
        :selected-hours="upsellHours"
        hint="Je kunt je keuze nog aanpassen tot je betaalt."
        @select="chooseUpsellHours"
      />

      <!-- VAT Disclaimer -->
      <div class="mt-6 text-xs text-gray-500 text-center">
        * Particulieren betalen inclusief 21% BTW. Zakelijke klanten betalen exclusief BTW (+ 21% BTW).
      </div>

      <!-- Not logged in: capture the e-mail address first, then log in inline or register -->
      <div v-if="!authStore.isAuthenticated" class="py-4">
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <!-- Step A: e-mail address -->
          <form v-if="leadPhase === 'email'" @submit.prevent="submitLead" novalidate>
            <h3 class="font-medium text-amber-900 mb-1">Bijna klaar: wat is je e-mailadres?</h3>
            <p class="text-sm text-amber-700 mb-4">
              Daarmee kunnen we je reservering bevestigen en krijg je je persoonlijke pincode voor de studiodeur.
            </p>
            <label for="lead_email" class="sr-only">E-mailadres</label>
            <div class="flex flex-col sm:flex-row gap-3">
              <input
                id="lead_email"
                v-model="leadForm.email"
                name="email"
                type="email"
                autocomplete="email"
                inputmode="email"
                required
                placeholder="je@voorbeeld.be"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:ring-amber-500 focus:border-amber-500"
                :aria-invalid="!!leadError"
                :aria-describedby="leadError ? 'lead_error' : undefined"
              />
              <button
                type="submit"
                class="btn btn-primary px-6 py-2 whitespace-nowrap"
                :disabled="leadSubmitting"
              >
                {{ leadSubmitting ? 'Even geduld...' : 'Ga verder' }}
              </button>
            </div>
            <p v-if="leadError" id="lead_error" class="mt-2 text-sm text-red-600" role="alert">{{ leadError }}</p>
            <p class="mt-3 text-xs text-amber-700">
              Al een account?
              <button type="button" class="underline font-medium" @click="leadPhase = 'login'">Log hier in</button>
            </p>
          </form>

          <!-- Step B: existing account, log in right here -->
          <form v-else-if="leadPhase === 'login'" @submit.prevent="submitInlineLogin" novalidate>
            <h3 class="font-medium text-amber-900 mb-1">Welkom terug</h3>
            <p class="text-sm text-amber-700 mb-4">
              Log in om je reservering af te ronden. Je keuze blijft bewaard.
            </p>
            <div class="space-y-3">
              <div>
                <label for="lead_login_email" class="block text-sm font-medium text-gray-700">E-mailadres</label>
                <input
                  id="lead_login_email"
                  v-model="leadForm.email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              <div>
                <label for="lead_login_password" class="block text-sm font-medium text-gray-700">Wachtwoord</label>
                <input
                  id="lead_login_password"
                  ref="leadPasswordInput"
                  v-model="leadForm.password"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:ring-amber-500 focus:border-amber-500"
                  :aria-invalid="!!leadError"
                  :aria-describedby="leadError ? 'lead_error' : undefined"
                />
              </div>
            </div>
            <p v-if="leadError" id="lead_error" class="mt-2 text-sm text-red-600" role="alert">{{ leadError }}</p>
            <div class="flex flex-col sm:flex-row gap-3 mt-4">
              <button type="submit" class="btn btn-primary px-6 py-2" :disabled="leadSubmitting">
                {{ leadSubmitting ? 'Inloggen...' : 'Inloggen en verdergaan' }}
              </button>
              <router-link
                :to="{ name: 'forgot-password', query: { email: leadForm.email || undefined } }"
                class="btn btn-secondary px-6 py-2 text-center"
              >
                Wachtwoord vergeten
              </router-link>
            </div>
            <p class="mt-3 text-xs text-amber-700">
              Toch geen account?
              <button type="button" class="underline font-medium" @click="redirectToRegister">Maak er een aan</button>
            </p>
          </form>
        </div>
      </div>

      <!-- Logged in user - show booking form -->
      <div v-else>
        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div class="flex items-center">
            <div class="shrink-0">
              <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">
                Ingelogd als {{ authStore.user?.name }}
              </h3>
              <p class="text-sm text-green-600">
                {{ authStore.user?.email }}
              </p>
            </div>
          </div>
        </div>

        <form @submit.prevent="submitBooking" class="space-y-6">
          <!-- Booking Purpose -->
          <div>
            <label for="bookingPurpose" class="block text-sm font-medium text-gray-700 mb-2">
              Doel van de reservering (optioneel)
            </label>
            <input
              id="bookingPurpose"
              v-model="customerForm.booking_purpose"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="bv. Klantnaam, projectnaam, evenement..."
              maxlength="255"
            />
            <p class="text-xs text-gray-500 mt-1">Helpt je bij het beheren van je reserveringen</p>
          </div>

          <!-- Order Reference (professional customers only) -->
          <div v-if="!isIndividualCustomer">
            <label for="orderReference" class="block text-sm font-medium text-gray-700 mb-2">
              Bestelreferentie (optioneel)
            </label>
            <input
              id="orderReference"
              v-model="customerForm.order_reference"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="bv. PO-nummer of interne referentie"
              maxlength="255"
            />
            <p class="text-xs text-gray-500 mt-1">Wordt vermeld op de verkoopfactuur</p>
          </div>

          <!-- Content Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-3">
              Type opname (optioneel)
            </label>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                v-for="option in contentTypeOptions"
                :key="option.value"
                type="button"
                @click="customerForm.content_type = customerForm.content_type === option.value ? null : option.value"
                :class="[
                  'p-3 border-2 rounded-lg transition-all duration-200 text-center hover:shadow-xs',
                  customerForm.content_type === option.value
                    ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                    : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <div class="text-xl mb-1">{{ option.icon }}</div>
                <div class="text-xs font-medium text-gray-900">{{ option.label }}</div>
              </button>
            </div>
          </div>

          <!-- Notes -->
          <div>
            <label for="customerNotes" class="block text-sm font-medium text-gray-700 mb-2">
              Opmerkingen (optioneel)
            </label>
            <textarea
              id="customerNotes"
              v-model="customerForm.notes"
              rows="3"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Speciale wensen of opmerkingen voor de studiosessie..."
            ></textarea>
          </div>

          <!-- Terms and Conditions -->
          <div class="flex items-start">
            <input
              id="acceptTerms"
              v-model="customerForm.acceptTerms"
              type="checkbox"
              required
              class="mt-1 w-4 h-4 text-amber-600 border-gray-300 rounded-sm focus:ring-amber-500"
            />
            <label for="acceptTerms" class="ml-2 text-sm text-gray-700">
              Ik ga akkoord met de
              <router-link to="/algemene-voorwaarden" class="text-amber-600 hover:text-amber-600 underline">algemene voorwaarden</router-link>
              en het
              <router-link to="/privacybeleid" class="text-amber-600 hover:text-amber-600 underline">privacybeleid</router-link>. *
            </label>
          </div>

          <!-- Herroepingsrecht consent (only for individual consumers) -->
          <div v-if="isIndividualCustomer" class="flex items-start">
            <input
              id="herroepingsrecht"
              v-model="customerForm.herroepingsrecht_accepted"
              type="checkbox"
              required
              class="mt-1 w-4 h-4 text-amber-600 border-gray-300 rounded-sm focus:ring-amber-500"
            />
            <label for="herroepingsrecht" class="ml-2 text-sm text-gray-700">
              Ik ga ermee akkoord dat de dienst wordt uitgevoerd voor het einde van de herroepingstermijn van 14 dagen en ik erken dat ik hiermee afstand doe van mijn
              <router-link to="/algemene-voorwaarden#herroepingsrecht" class="text-amber-600 hover:text-amber-600 underline">herroepingsrecht</router-link>. *
            </label>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center justify-between pt-6 border-t">
            <button
              type="button"
              @click="previousStep"
              class="btn btn-secondary px-6 py-2"
            >
              Vorige
            </button>
            
            <button
              type="submit"
              :disabled="!canSubmitBooking || submitting"
              class="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 flex items-center"
            >
              <LoadingSpinner v-if="submitting" size="sm" class="mr-2" />
              {{ submitting ? 'Reservering maken...' : 'Reservering maken' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Error Messages -->
    <ErrorMessage
      v-if="error"
      :message="error"
      @dismiss="error = null"
      :dismissible="true"
      class="mt-6"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import BookingCalendar from '@/components/BookingCalendar.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import TierBadge from '@/components/TierBadge.vue'
import UpsellExtensionPicker from '@/components/UpsellExtensionPicker.vue'
import { useApi } from '@/composables/useApi'
import { useAuthStore } from '@/stores/auth'
import { useLoyalty } from '@/composables/useLoyalty'
import { STUDIO_TIMEZONE, shouldShowTimezoneWarning, getTimezoneDisplayName, convertBookingTimeToUserTimezone, zonedTimeToInstant } from '@/utils/timezone'
import { attributionPayload } from '@/utils/attribution'
import { trackEvent } from '@/utils/analytics'
import { parseDurationParam } from '@/utils/pendingBooking'
import type { TimeSlot, UpsellExtension } from '@/types'

interface CustomerForm {
  customer_type: 'individual' | 'professional'
  customer_first_name: string
  customer_last_name: string
  customer_email: string
  customer_phone: string
  customer_street: string
  customer_city: string
  customer_postal_code: string
  customer_country: string
  company_name: string
  vat_number: string
  booking_purpose: string
  order_reference: string
  content_type: 'photoshoot' | 'video' | 'mix' | null
  notes: string
  acceptTerms: boolean
  herroepingsrecht_accepted: boolean
}

const router = useRouter()
const route = useRoute()
const api = useApi()
const authStore = useAuthStore()
const { currentTier, fetchMyTier } = useLoyalty()

// State
const currentStep = ref(1)
const selectedDuration = ref<number | null>(null)
// Flexible booking (loyalty perk): self-chosen whole-hour start/end
const flexibleMode = ref(false)
const selectedDate = ref<Date | null>(null)
const selectedSlot = ref<TimeSlot | null>(null)
const submitting = ref(false)

// Verleng-upsell (stap 2 en 3): 0, 1 of 2 extra uren bovenop het gekozen
// pakket, tegen het gemiddelde uurtarief van dat pakket. De opties komen van
// de backend voor het gekozen slot; de keuze gaat mee bij het aanmaken van de
// boeking en blijft op de betaalpagina aanpasbaar.
const upsellHours = ref(0)
const upsellQuote = ref<UpsellExtension | null>(null)
let upsellQuoteRequest = 0
const error = ref<string | null>(null)

// Lead capture (step 3, not logged in): e-mail first, then inline login or registration.
// The e-mail address is stored server-side together with the chosen slot so an
// abandoned booking can be recovered by mail (see BookingLeadController).
type LeadPhase = 'email' | 'login'
const leadPhase = ref<LeadPhase>('email')
const leadForm = ref({ email: '', password: '' })
const leadError = ref<string | null>(null)
const leadSubmitting = ref(false)
const leadPasswordInput = ref<HTMLInputElement | null>(null)
const LEAD_EMAIL_KEY = 'pendingBookingEmail'

// Choice restored from a recovery link (/?lead=TOKEN); handed to the calendar
// so the date and slot are preselected again.
interface RecoveredLead {
  email: string
  has_account: boolean
  flexible: boolean
  duration_hours: number | null
  booking_date: string | null
  start_time: string | null
  end_time: string | null
  upcoming: boolean
}
const recoveredLead = ref<RecoveredLead | null>(null)
const recoveryNotice = ref<string | null>(null)

const customerForm = ref<CustomerForm>({
  customer_type: 'individual',
  customer_first_name: '',
  customer_last_name: '',
  customer_email: '',
  customer_phone: '',
  customer_street: '',
  customer_city: '',
  customer_postal_code: '',
  customer_country: 'BE',
  company_name: '',
  vat_number: '',
  booking_purpose: '',
  order_reference: '',
  content_type: null,
  notes: '',
  acceptTerms: false,
  herroepingsrecht_accepted: false
})

// Duration options (loaded from API)
const durationOptions = ref<Array<{
  hours: number
  price: string
  description: string
  price_cents: number
  is_popular: boolean
}>>([])

const loadingPricing = ref(false)

// Content type options
const contentTypeOptions: { value: 'photoshoot' | 'video' | 'mix'; label: string; icon: string }[] = [
  { value: 'photoshoot', label: 'Fotoshoot', icon: '📸' },
  { value: 'video', label: 'Video', icon: '🎥' },
  { value: 'mix', label: 'Mix', icon: '🎯' }
]

// Computed
const selectedDurationOption = computed(() => {
  return durationOptions.value.find(option => option.hours === selectedDuration.value)
})

const isIndividualCustomer = computed(() => {
  return (authStore.user?.customer_type ?? 'individual') === 'individual'
})

const canSubmitBooking = computed(() => {
  return selectedSlot.value &&
         authStore.isAuthenticated &&
         customerForm.value.acceptTerms &&
         (!isIndividualCustomer.value || customerForm.value.herroepingsrecht_accepted)
})

const selectedUpsellOption = computed(() => {
  if (!upsellHours.value || !upsellQuote.value) return null
  return upsellQuote.value.options.find(o => o.hours === upsellHours.value) ?? null
})

const upsellPriceCents = computed(() => selectedUpsellOption.value?.price_cents ?? 0)

const totalDurationHours = computed(() => (selectedDuration.value ?? 0) + upsellHours.value)

// Eindtijd van de sessie inclusief gekozen verlenging ("HH:MM")
const sessionEndTime = computed(() => {
  if (!selectedSlot.value) return ''
  if (selectedUpsellOption.value) return selectedUpsellOption.value.new_end_time
  return selectedSlot.value.end_time.substring(0, 5)
})

const showUpsellPicker = computed(() => {
  const q = upsellQuote.value
  if (!q || !q.eligible) return false
  return upsellHours.value > 0 || q.options.some(o => o.available)
})

const displayPrice = computed(() => {
  if (!selectedSlot.value) return '€0,00'

  const baseCents = selectedSlot.value.price_cents ?? 0
  const formatted = (cents: number) => `€${(cents / 100).toFixed(2).replace('.', ',')}*`

  // Met verlenging: pakketprijs + extra uren aan het gemiddelde uurtarief
  if (upsellPriceCents.value > 0) {
    return formatted(baseCents + upsellPriceCents.value)
  }

  // In step 3, always show asterisk since customer type selection was removed
  if (currentStep.value === 3) {
    return formatted(baseCents)
  }

  // For other steps, use the backend formatted price
  return selectedSlot.value.price_formatted || '€0,00'
})

// Only tiers with the custom_time_slots benefit and a configured tier-specific
// hourly rate may book flexibly
const canBookFlexible = computed(() => {
  const benefits = currentTier.value?.benefits
  return authStore.isAuthenticated
    && !!benefits?.custom_time_slots
    && (benefits?.flexible_hourly_rate_cents ?? 0) > 0
})

const flexibleRateFormatted = computed(() => {
  const cents = currentTier.value?.benefits?.flexible_hourly_rate_cents ?? 0
  return '€' + (cents / 100).toFixed(2).replace('.', ',')
})

// Methods
// Umami-funnel van de boekingsflow: event "Booking Step" met step =
// duration | date | slot | next | back | login | register | submit | created | failed
// (+ src = klikpagina op boenkop.be, zie trackEvent in utils/analytics)
const selectDuration = (hours: number) => {
  selectedDuration.value = hours
  trackEvent('Booking Step', { step: 'duration', hours: `${hours}u` })
  if (flexibleMode.value) {
    flexibleMode.value = false
    selectedSlot.value = null
  }
}

const selectFlexible = () => {
  flexibleMode.value = true
  trackEvent('Booking Step', { step: 'duration', hours: 'flex' })
  selectedDuration.value = null
  selectedSlot.value = null
}

const nextStep = () => {
  if (currentStep.value < 3) {
    trackEvent('Booking Step', { step: 'next', from: `stap ${currentStep.value}` })
    currentStep.value++
    
    // On mobile, scroll to top of form when moving to step 3
    if (currentStep.value === 3 && window.innerWidth < 1024) {
      nextTick(() => {
        // Find the step 3 container
        const step3Container = document.querySelector('.step-3')
        if (step3Container) {
          // Get the booking form container
          const bookingForm = step3Container.closest('.booking-form')
          if (bookingForm) {
            // Scroll to the top of the booking form with some offset
            const offset = 20 // Small offset from top
            const elementPosition = bookingForm.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - offset
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }
        }
      })
    }
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    trackEvent('Booking Step', { step: 'back', from: `stap ${currentStep.value}` })
    currentStep.value--
  }
}

const onSlotSelected = (slot: TimeSlot | null) => {
  selectedSlot.value = slot
  error.value = null
  if (slot) trackEvent('Booking Step', { step: 'slot', hours: flexibleMode.value ? 'flex' : `${selectedDuration.value}u` })

  // Flexible slots carry their own duration (end hour - start hour)
  if (flexibleMode.value) {
    selectedDuration.value = (slot as any)?.duration_hours ?? null
  }
}

const onDateChanged = (date: Date | null) => {
  selectedDate.value = date
  if (date) trackEvent('Booking Step', { step: 'date', hours: flexibleMode.value ? 'flex' : `${selectedDuration.value}u` })
  // Clear slot selection when date changes
  if (selectedSlot.value) {
    selectedSlot.value = null
  }
}

const formatSelectedDate = (): string => {
  if (!selectedDate.value) return ''
  return selectedDate.value.toLocaleDateString('nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatSelectedTime = (): string => {
  if (!selectedSlot.value) return ''
  const startTime = selectedSlot.value.start_time.substring(0, 5)
  return `${startTime} - ${sessionEndTime.value}`
}

// Verlengopties ophalen voor het gekozen slot. Een eerder gekozen aantal uren
// blijft staan als die optie nog beschikbaar is, anders valt het terug op 0.
const loadUpsellQuote = async () => {
  const slot = selectedSlot.value
  const date = selectedDate.value
  const duration = selectedDuration.value
  const requestId = ++upsellQuoteRequest

  if (!slot || !date || !duration) {
    upsellQuote.value = null
    upsellHours.value = 0
    return
  }

  try {
    const response = await api.get('/availability/upsell-options', {
      date: formatDateKey(date),
      start_time: slot.start_time.substring(0, 5),
      duration_hours: duration,
      flexible: flexibleMode.value ? 1 : 0,
      customer_type: authStore.user?.customer_type ?? customerForm.value.customer_type,
    })
    if (requestId !== upsellQuoteRequest) return

    const quote: UpsellExtension | null = response.success ? (response.data?.upsell ?? null) : null
    upsellQuote.value = quote

    const stillAvailable = quote?.eligible && quote.options.some(o => o.hours === upsellHours.value && o.available)
    if (!stillAvailable) upsellHours.value = 0
  } catch (err) {
    if (requestId !== upsellQuoteRequest) return
    console.error('Failed to load upsell extension options:', err)
    upsellQuote.value = null
    upsellHours.value = 0
  }
}

const chooseUpsellHours = (hours: number) => {
  if (hours === upsellHours.value) return
  const option = upsellQuote.value?.options.find(o => o.hours === hours)
  if (hours > 0 && (!option || !option.available)) return
  upsellHours.value = hours
  trackEvent('Booking Step', { step: 'upsell', hours: `${hours}u` })
}

watch([selectedSlot, selectedDate, selectedDuration, flexibleMode], () => {
  void loadUpsellQuote()
})

// Timezone methods
// The warning compares the visitor's clock with the studio's at the moment of
// the chosen slot (offsets, not zone names: Amsterdam and Paris share
// Brussels' clock and must not get the warning).
const timezoneDiffers = computed(() => {
  if (!selectedSlot.value || !selectedDate.value) return false
  const at = zonedTimeToInstant(formatDateKey(selectedDate.value), selectedSlot.value.start_time, STUDIO_TIMEZONE)
  return shouldShowTimezoneWarning(STUDIO_TIMEZONE, at ?? undefined)
})

const convertToUserTimezone = (): string => {
  if (!selectedSlot.value || !selectedDate.value) return ''

  const converted = convertBookingTimeToUserTimezone(
    formatDateKey(selectedDate.value),
    selectedSlot.value.start_time,
    STUDIO_TIMEZONE
  )

  return `${converted.date} ${converted.time} (${getTimezoneDisplayName(converted.timezone)})`
}

// Snapshot of the flow so the visitor lands back on step 3 after login/registration
const pendingBookingState = () => ({
  selectedDuration: selectedDuration.value,
  flexibleMode: flexibleMode.value,
  selectedDate: selectedDate.value?.toISOString(),
  selectedSlot: selectedSlot.value,
  upsellHours: upsellHours.value,
  currentStep: 3, // Always go to step 3 after login (ready to complete booking)
  notes: customerForm.value.notes
})

const rememberPendingBooking = () => {
  sessionStorage.setItem('pendingBooking', JSON.stringify(pendingBookingState()))
  if (leadForm.value.email) {
    sessionStorage.setItem(LEAD_EMAIL_KEY, leadForm.value.email)
  }
}

const redirectToRegister = () => {
  trackEvent('Booking Step', { step: 'register' })
  rememberPendingBooking()
  router.push({
    name: 'register',
    query: { redirect: router.currentRoute.value.fullPath, email: leadForm.value.email || undefined }
  })
}

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

// Step 3, not logged in: store the lead, then route to login (inline) or registration
const submitLead = async () => {
  const email = leadForm.value.email.trim()
  leadError.value = null

  if (!isValidEmail(email)) {
    leadError.value = 'Vul een geldig e-mailadres in.'
    return
  }
  leadForm.value.email = email

  try {
    leadSubmitting.value = true

    const response = await api.post<{ lead_id: string; has_account: boolean }>('/booking-leads', {
      email,
      flexible: flexibleMode.value,
      duration_hours: selectedDuration.value,
      booking_date: selectedDate.value ? formatDateKey(selectedDate.value) : null,
      start_time: selectedSlot.value?.start_time ?? null,
      end_time: selectedSlot.value?.end_time ?? null,
      price_cents: selectedSlot.value?.price_cents ?? null,
      ...attributionPayload()
    })

    if (!response.success) {
      throw new Error(response.error?.message || 'Er ging iets mis')
    }

    const hasAccount = !!response.data?.has_account
    trackEvent('Booking Step', { step: 'lead', account: hasAccount ? 'bestaand' : 'nieuw' })

    if (hasAccount) {
      leadPhase.value = 'login'
      await nextTick()
      leadPasswordInput.value?.focus()
    } else {
      redirectToRegister()
    }
  } catch (err: any) {
    console.error('Failed to store booking lead:', err)
    // Lead capture must never block the booking: fall back to the register page
    if (err?.response?.status === 422) {
      leadError.value = 'Vul een geldig e-mailadres in.'
    } else {
      redirectToRegister()
    }
  } finally {
    leadSubmitting.value = false
  }
}

// Step 3, existing account: log in without leaving the booking flow
const submitInlineLogin = async () => {
  leadError.value = null

  if (!isValidEmail(leadForm.value.email.trim()) || !leadForm.value.password) {
    leadError.value = 'Vul je e-mailadres en wachtwoord in.'
    return
  }

  try {
    leadSubmitting.value = true
    await authStore.login({ email: leadForm.value.email.trim(), password: leadForm.value.password })
    leadForm.value.password = ''
    sessionStorage.removeItem(LEAD_EMAIL_KEY)
    trackEvent('Booking Step', { step: 'inline_login' })

    try {
      await fetchMyTier()
    } catch (e) {
      console.error('Failed to fetch tier info:', e)
    }
  } catch (err: any) {
    leadError.value = err?.message || 'Inloggen mislukt. Controleer je gegevens.'
  } finally {
    leadSubmitting.value = false
  }
}

// Recovery mail link (/?lead=TOKEN): put the visitor back on the calendar with
// their duration, date and slot preselected and the e-mail address filled in.
const restoreFromRecoveryLink = async (token: string) => {
  try {
    const response = await api.get<RecoveredLead>(`/booking-leads/${encodeURIComponent(token)}`)
    if (!response.success || !response.data) return

    const lead = response.data
    recoveredLead.value = lead
    leadForm.value.email = lead.email
    // Flexible slots are a loyalty perk: only restore them for a logged-in
    // member, otherwise fall back to the fixed duration of the same length.
    flexibleMode.value = !!lead.flexible && canBookFlexible.value
    const knownDuration = durationOptions.value.some(option => option.hours === lead.duration_hours)
    if (!flexibleMode.value && knownDuration) {
      selectedDuration.value = lead.duration_hours
    }

    if (selectedDuration.value || flexibleMode.value) {
      currentStep.value = 2
    }

    recoveryNotice.value = lead.upcoming
      ? 'Welkom terug! Je eerdere keuze staat klaar. Controleer datum en tijd en ga verder.'
      : 'Welkom terug! De datum die je eerder koos is intussen voorbij. Kies een nieuwe datum.'

    trackEvent('Booking Step', { step: 'recovery_link', account: lead.has_account ? 'bestaand' : 'nieuw' })
  } catch (err) {
    console.error('Failed to restore booking lead:', err)
  } finally {
    // Drop the token from the address bar so a refresh or shared URL does not replay it
    const query = { ...route.query }
    delete query.lead
    router.replace({ query })
  }
}

const loadPricingOptions = async () => {
  try {
    loadingPricing.value = true
    // For steps 1 and 2, use 'individual' as default since user hasn't chosen customer type yet
    const response = await api.get('/availability/pricing', {
      params: {
        customer_type: 'individual'
      }
    })
    
    if (response.success && response.data?.rates) {
      durationOptions.value = response.data.rates.map((rate: any) => ({
        hours: rate.duration_hours,
        price: `${(rate.price_cents / 100).toFixed(0)}`,
        price_cents: rate.price_cents,
        description: rate.description || `${rate.duration_hours} uur studiosessie`,
        is_popular: rate.is_popular || false,
        vat_breakdown: rate.vat_breakdown
      }))
    }
  } catch (error) {
    console.error('Failed to load pricing options:', error)
    // If API fails, show error instead of hardcoded values
    durationOptions.value = []
  } finally {
    loadingPricing.value = false
  }
}

const restoreBookingState = async () => {
  const pendingBooking = sessionStorage.getItem('pendingBooking')
  if (pendingBooking && authStore.isAuthenticated) {
    try {
      const state = JSON.parse(pendingBooking)

      selectedDuration.value = state.selectedDuration
      flexibleMode.value = !!state.flexibleMode
      currentStep.value = state.currentStep || 3 // Go to step 3 since they're now logged in
      customerForm.value.notes = state.notes || ''
      
      if (state.selectedDate) {
        selectedDate.value = new Date(state.selectedDate)
      }
      
      if (state.selectedSlot) {
        selectedSlot.value = state.selectedSlot
        // Emit the slot to notify parent components
        await nextTick()
      }

      // Gekozen verlenging terugzetten; de quote-watch controleert of ze nog kan
      const restoredUpsell = Number(state.upsellHours)
      upsellHours.value = [1, 2].includes(restoredUpsell) ? restoredUpsell : 0
      
      // Clear the pending state after restoring
      sessionStorage.removeItem('pendingBooking')
    } catch (e) {
      console.error('Failed to restore booking state:', e)
    }
  }
}

const formatDateKey = (date: Date): string => {
  // Use local timezone to avoid off-by-one errors
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const submitBooking = async () => {
  if (!selectedSlot.value || !selectedDate.value) {
    error.value = 'Selecteer eerst een datum en tijd.'
    return
  }

  if (!authStore.isAuthenticated || !authStore.user) {
    error.value = 'Je moet ingelogd zijn om een reservering te maken.'
    return
  }

  try {
    submitting.value = true
    error.value = null
    trackEvent('Booking Step', { step: 'submit', hours: flexibleMode.value ? 'flex' : `${selectedDuration.value}u` })

    const bookingData = {
      booking_date: formatDateKey(selectedDate.value), // Use local date to avoid timezone issues
      start_time: selectedSlot.value.start_time.substring(0, 5), // Remove seconds (HH:MM:SS -> HH:MM)
      duration_hours: selectedDuration.value,
      upsell_extension_hours: upsellHours.value,
      flexible: flexibleMode.value,
      customer_type: customerForm.value.customer_type,
      customer_first_name: customerForm.value.customer_first_name,
      customer_last_name: customerForm.value.customer_last_name,
      customer_email: customerForm.value.customer_email,
      customer_phone: customerForm.value.customer_phone,
      customer_street: customerForm.value.customer_street,
      customer_city: customerForm.value.customer_city,
      customer_postal_code: customerForm.value.customer_postal_code,
      customer_country: customerForm.value.customer_country,
      company_name: customerForm.value.company_name,
      vat_number: customerForm.value.vat_number,
      booking_purpose: customerForm.value.booking_purpose.trim() || null,
      order_reference: isIndividualCustomer.value ? null : (customerForm.value.order_reference.trim() || null),
      content_type: customerForm.value.content_type,
      notes: customerForm.value.notes.trim() || null,
      herroepingsrecht_accepted: isIndividualCustomer.value ? customerForm.value.herroepingsrecht_accepted : null,
      // Campagne-attributie (utm_*) waarmee de klant op de site/het portaal kwam
      ...attributionPayload(),
    }


    const response = await api.post('/bookings', bookingData)


    if (response.success && response.data) {
      trackEvent('Booking Step', { step: 'created', hours: flexibleMode.value ? 'flex' : `${selectedDuration.value}u` })
      // Clear pending booking state
      sessionStorage.removeItem('pendingBooking')
      
      // Redirect to payment page or confirmation
      router.push({
        name: 'booking-payment',
        params: { bookingId: response.data.booking.id }
      })
    } else {
      throw new Error(response.error?.message || 'Reservering kon niet worden aangemaakt.')
    }
  } catch (err: any) {
    console.error('Booking creation error:', err)
    trackEvent('Booking Step', { step: 'failed' })

    // Extract error message from API response
    if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else if (err.response?.data?.errors) {
      // Handle Laravel validation errors (422)
      const errors = err.response.data.errors
      const firstError = Object.values(errors)[0]
      error.value = Array.isArray(firstError) ? firstError[0] : firstError
    } else if (err.response?.data?.error) {
      error.value = err.response.data.error
    } else if (err.message) {
      error.value = err.message
    } else {
      error.value = 'Er is een fout opgetreden. Probeer het opnieuw.'
    }

    // Scroll to error message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } finally {
    submitting.value = false
  }
}

// Watch for authentication changes to restore booking state
watch(() => authStore.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    await restoreBookingState()
    
    // Pre-fill customer form with user data if available
    if (authStore.user) {
      customerForm.value = {
        ...customerForm.value,
        customer_first_name: authStore.user.first_name || '',
        customer_last_name: authStore.user.last_name || '',
        customer_email: authStore.user.email || '',
        customer_phone: authStore.user.phone || '',
        customer_street: authStore.user.customer_street || '',
        customer_city: authStore.user.customer_city || '',
        customer_postal_code: authStore.user.customer_postal_code || '',
        customer_country: authStore.user.customer_country || 'BE',
        customer_type: authStore.user.customer_type || 'individual',
        company_name: authStore.user.company_name || '',
        vat_number: authStore.user.vat_number || ''
      }
    }
  }
})

// VAT calculation methods
const getVatBreakdown = () => {
  if (!selectedSlot.value) return { subtotal_cents: 0, vat_cents: 0, total_cents: 0 }
  
  const totalCents = selectedSlot.value.price * 100 // Convert to cents
  const vatRate = 0.21
  
  if (customerForm.value.customer_type === 'professional') {
    // For professionals, total includes VAT (base × 1.21)
    const baseCents = Math.round(totalCents / (1 + vatRate))
    const vatCents = totalCents - baseCents
    
    return {
      subtotal_cents: baseCents,
      vat_cents: vatCents,
      total_cents: totalCents
    }
  } else {
    // For individuals, total is VAT-inclusive
    const baseCents = Math.round(totalCents / (1 + vatRate))
    const vatCents = totalCents - baseCents
    
    return {
      subtotal_cents: baseCents,
      vat_cents: vatCents,
      total_cents: totalCents
    }
  }
}

const formatVatAmount = (cents: number): string => {
  return '€' + (cents / 100).toFixed(2).replace('.', ',')
}

// Watch for customer type changes and reload pricing
watch(() => customerForm.value.customer_type, async () => {
  await loadPricingOptions()
  // Reset selected duration when customer type changes to ensure pricing is correct
  if (selectedDuration.value) {
    const currentDuration = selectedDuration.value
    selectedDuration.value = null
    selectedSlot.value = null
    // Reselect the duration to update prices
    nextTick(() => {
      selectedDuration.value = currentDuration
    })
  }
})

// Initialize component
onMounted(async () => {
  // Load pricing options first
  await loadPricingOptions()

  // If we have a token but no user data, fetch the user
  if (authStore.token && !authStore.user) {
    try {
      await authStore.fetchUser()
    } catch (error) {
      console.error('Failed to fetch user:', error)
      // Clear invalid token state
      authStore.clearAuthState()
    }
  }

  // Fetch tier info if authenticated
  if (authStore.isAuthenticated) {
    try {
      await fetchMyTier()
    } catch (error) {
      console.error('Failed to fetch tier info:', error)
    }
  }

  // Try to restore booking state if user is already authenticated
  await restoreBookingState()

  // E-mail address left behind on a previous visit to step 3
  leadForm.value.email = sessionStorage.getItem(LEAD_EMAIL_KEY) || ''

  // Recovery mail link
  const leadToken = route.query.lead
  if (typeof leadToken === 'string' && leadToken) {
    await restoreFromRecoveryLink(leadToken)
  }

  // ?duur=2|4|10 from boenkop.be (pricing cards): preselect that duration and
  // go straight to the calendar. A restored booking or recovery link wins.
  if (currentStep.value === 1 && !selectedDuration.value && !flexibleMode.value) {
    const hours = parseDurationParam(route.query.duur, durationOptions.value.map(option => option.hours))
    if (hours) {
      selectedDuration.value = hours
      currentStep.value = 2
      trackEvent('Booking Step', { step: 'duration', hours: `${hours}u`, via: 'link' })
    }
  }
})
</script>

<style scoped>
@reference "../style.css";

.btn {
  @apply font-medium rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2;
}

.btn-primary {
  @apply bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500;
}

.btn-secondary {
  @apply bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500;
}

/* Custom checkbox styling */
input[type="checkbox"]:checked {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Form focus states */
input:focus,
textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>